import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, LayoutTemplate, Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import * as api from "../lib/api";

const PRIMARY = "#018D4C";
const EMPTY = { name: "", description: "", instructions: "" };
const PLACEHOLDER_INSTRUCTIONS = `Audience: the client's marketing lead, not technical.
Sections, in order: Where we are / What moved / Why / What we do next.
Compare: this month vs last month, plus same month last year.
Tone: plain and confident. Gloss every SEO term. No exclamation points.
Sign off as: Acme SEO
Accent: #018D4C`;

function TemplateDialog({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(initial ? { name: initial.name, description: initial.description, instructions: initial.instructions } : EMPTY);
      setError(null);
    }
  }, [open, initial]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const saved = initial
        ? await api.updateReportTemplate(initial.id, form)
        : await api.createReportTemplate(form);
      onSaved(saved);
    } catch (err) {
      setError(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Couldn't save template.");
    } finally {
      setSaving(false);
    }
  };

  const field = "w-full px-3 py-2.5 text-sm border border-zinc-200 bg-zinc-50 rounded-lg focus:outline-none focus:border-zinc-400 placeholder-zinc-400";

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl p-0 gap-0">
        <form onSubmit={handleSubmit}>
          <div className="p-5 space-y-4">
            <DialogTitle className="text-base font-bold text-zinc-900">{initial ? "Edit template" : "New template"}</DialogTitle>
            <div>
              <label className="block text-sm font-medium text-zinc-900 mb-1.5">Name</label>
              <input
                required
                maxLength={120}
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Monthly client check-in"
                className={field}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-900 mb-1.5">Description</label>
              <input
                maxLength={300}
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="The monthly update we send retainer clients."
                className={field}
              />
              <p className="text-[11px] text-zinc-400 mt-1">One line saying when to use it. This is what an agent reads to decide.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-900 mb-1.5">Instructions</label>
              <textarea
                rows={9}
                maxLength={8000}
                value={form.instructions}
                onChange={e => setForm(f => ({ ...f, instructions: e.target.value }))}
                placeholder={PLACEHOLDER_INSTRUCTIONS}
                className={`${field} font-mono text-xs leading-relaxed resize-y`}
              />
              <p className="text-[11px] text-zinc-400 mt-1">
                Brand voice for the whole org lives in Settings › Business context.
              </p>
            </div>
            {error && <p className="text-xs text-red-500">{String(error)}</p>}
          </div>
          <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-zinc-100 bg-zinc-50 rounded-b-lg">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-zinc-700 hover:text-zinc-900">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.name.trim()}
              className="px-4 py-2 text-sm font-bold text-white rounded-lg disabled:opacity-40"
              style={{ backgroundColor: PRIMARY }}
            >
              {saving ? "Saving…" : initial ? "Save changes" : "Create template"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ReportTemplatesPage() {
  const [templates, setTemplates] = useState(null);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState({ open: false, initial: null });

  useEffect(() => {
    api.getReportTemplates()
      .then(d => setTemplates(d.templates || []))
      .catch(() => setError("Couldn't load templates."));
  }, []);

  const handleSaved = (saved) => {
    setTemplates(ts => {
      const rest = (ts || []).filter(t => t.id !== saved.id);
      return [saved, ...rest];
    });
    setDialog({ open: false, initial: null });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this template? Reports already written with it are kept.")) return;
    await api.deleteReportTemplate(id);
    setTemplates(ts => ts.filter(t => t.id !== id));
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Link to="/dashboard/reports" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3">
        <ChevronLeft size={14} /> Reports
      </Link>
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-bold text-zinc-900">Report templates</h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Reusable briefs your agents follow when they write a report: who it is for, which sections it has, and how it should sound.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setDialog({ open: true, initial: null })}
          className="shrink-0 flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white rounded-lg"
          style={{ backgroundColor: PRIMARY }}
        >
          <Plus size={16} strokeWidth={2} /> New template
        </button>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}
      {!templates && !error && <p className="text-sm text-zinc-400">Loading…</p>}

      {templates && templates.length === 0 && (
        <div className="border border-dashed border-zinc-200 rounded-xl py-10 px-6 flex flex-col items-center text-center">
          <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center mb-3">
            <LayoutTemplate size={18} strokeWidth={2} className="text-zinc-600" />
          </div>
          <p className="text-sm font-bold text-zinc-900">No templates yet</p>
          <p className="text-xs text-zinc-500 mt-1 max-w-xs">
            A template is a reusable brief for a kind of report: who it is for, which sections it has, how it sounds.
          </p>
        </div>
      )}

      {templates && templates.length > 0 && (
        <div className="bg-white rounded-xl border border-zinc-100 shadow-sm divide-y divide-zinc-100">
          {templates.map(t => (
            <div key={t.id} className="flex items-start gap-3 px-5 py-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-zinc-900">{t.name}</p>
                {t.description && <p className="text-xs text-zinc-500 mt-0.5">{t.description}</p>}
              </div>
              <button type="button" onClick={() => setDialog({ open: true, initial: t })} className="text-zinc-400 hover:text-zinc-900 p-1" title="Edit">
                <Pencil size={14} strokeWidth={2} />
              </button>
              <button type="button" onClick={() => handleDelete(t.id)} className="text-zinc-300 hover:text-red-500 p-1" title="Delete">
                <Trash2 size={14} strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>
      )}

      <TemplateDialog
        open={dialog.open}
        initial={dialog.initial}
        onClose={() => setDialog({ open: false, initial: null })}
        onSaved={handleSaved}
      />
    </div>
  );
}
